import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { UniversalCamera } from '@babylonjs/core/Cameras/universalCamera';
import type { Scene } from '@babylonjs/core/scene';

/**
 * How far the clamp keeps the camera from the wall plane, inside the room's own half-extent.
 *
 * Has to clear `camera.minZ` (0.05m, below) with real margin: the failure mode is not "the
 * camera looks like it's touching the wall," it's "the wall's own inside face falls behind the
 * near clip plane and the room renders as if the wall were not there" (CLAUDE.md, "Touching a
 * plane is a signed test"; this is the same near-clip class of bug from the other side). 0.4m is
 * comfortably clear of that at any FOV this app uses, and still close enough that a visitor
 * standing at the boundary reads as standing near a wall, not stopped well short of one.
 */
const WALL_INSET_METRES = 0.4;

/**
 * Flat-screen camera: WASD to walk, drag to look.
 *
 * Not a lesser fallback — it is how the client gets tested. Two browser tabs on a desktop
 * can run a full multiplayer session, which is the only practical way to exercise presence
 * and locks without two headsets in the room.
 */
export class DesktopControls {
  readonly camera: UniversalCamera;

  /** Removes the eye-height lock's `onBeforeRenderObservable` registration below — see
   *  `dispose()`'s own doc for why this has to run, and run first. */
  private readonly stopGravityLock: () => void;

  /**
   * Half the room's own width/depth, minus `WALL_INSET_METRES` — `Infinity` until the first
   * `setRoomExtents()` call, so a desktop session built before any room has ever loaded (there
   * is no such caller today, but nothing here should assume one) walks unclamped rather than
   * clamping to a boundary of zero.
   */
  private clampHalfWidth = Infinity;
  private clampHalfDepth = Infinity;

  constructor(scene: Scene, canvas: HTMLCanvasElement) {
    // Eye height, so the desktop view frames the room the same way a standing user sees it.
    this.camera = new UniversalCamera('desktopCamera', new Vector3(0, 1.6, -3.2), scene);
    this.camera.setTarget(new Vector3(0, 1.4, 0));

    this.camera.attachControl(canvas, true);

    // WASD rather than Babylon's arrow-key default.
    this.camera.keysUp = [87];
    this.camera.keysDown = [83];
    this.camera.keysLeft = [65];
    this.camera.keysRight = [68];

    this.camera.speed = 0.28;
    this.camera.angularSensibility = 3600;
    this.camera.inertia = 0.65;
    this.camera.minZ = 0.05;

    // Walking, not flying: the camera stays at eye height regardless of where it is aimed.
    this.camera.applyGravity = false;
    const observer = scene.onBeforeRenderObservable.add(() => {
      this.camera.position.y = 1.6;

      // The room boundary (plan/session-exit-and-membership-fixes.md, A3): a desktop visitor
      // used to walk straight through the walls and out into the skybox, since nothing here
      // touched X/Z at all. Clamped in the same per-frame hook the eye-height lock already
      // uses, against whatever `setRoomExtents()` was last told — never a hardcoded size, since
      // `SceneRoot.buildRoom()` rebuilds against a `RoomTemplateDto` that can differ per room
      // and per resync.
      if (this.camera.position.x > this.clampHalfWidth) {
        this.camera.position.x = this.clampHalfWidth;
      } else if (this.camera.position.x < -this.clampHalfWidth) {
        this.camera.position.x = -this.clampHalfWidth;
      }

      if (this.camera.position.z > this.clampHalfDepth) {
        this.camera.position.z = this.clampHalfDepth;
      } else if (this.camera.position.z < -this.clampHalfDepth) {
        this.camera.position.z = -this.clampHalfDepth;
      }
    });
    this.stopGravityLock = () => scene.onBeforeRenderObservable.remove(observer);
  }

  /**
   * Tells the clamp the current room's own extents — called once after the initial
   * `SceneRoot.buildRoom()` and again on every rebuild (`main.ts`'s `hub.onResynced`), since a
   * resync's template can genuinely differ from the room this session started in. `width`/`depth`
   * are the room's full span (`RoomTemplateDto.floorplan`), centred on `SceneRoot.roomOrigin`,
   * which never moves off the scene origin — halving them here, not at each call site, keeps
   * "the room is centred at the origin" a fact this class states once rather than one every
   * caller has to restate correctly.
   */
  setRoomExtents(width: number, depth: number): void {
    this.clampHalfWidth = Math.max(width / 2 - WALL_INSET_METRES, 0);
    this.clampHalfDepth = Math.max(depth / 2 - WALL_INSET_METRES, 0);
  }

  /**
   * Was `this.camera.dispose()` alone, with nothing to stop the eye-height lock above — this
   * class had no caller anywhere outside a test until the `/room` route's own cleanup started
   * calling it (`main.ts`). Left unremoved, that observer keeps writing
   * `this.camera.position.y = 1.6` every frame to a camera this same `dispose()` call has
   * already disposed, for as long as the scene it was registered on keeps rendering — a
   * second `/room` mount is a live path for exactly that (Router exists specifically to make
   * it one), so `stopGravityLock()` has to run, and run *before* `this.camera.dispose()`, not
   * after: the observer's own callback closes over `this.camera` directly, and Babylon does
   * not guarantee an in-flight render tick sees a `dispose()` that happened between the two.
   */
  dispose(): void {
    this.stopGravityLock();
    this.camera.dispose();
  }
}
